/**
 * MDM-MOBILE-P1-001: Grading flow orchestrator hook
 *
 * Manages grading flow state machine:
 * idle -> capturing -> processing_image -> uploading -> grading -> results -> feedback
 *
 * Coordinates useCamera, useImagePipeline, and gradingApi calls.
 * Emits telemetry events at each state transition.
 */
import { useState, useCallback, useRef, createContext, useContext } from 'react';
import { getUploadUrl, uploadToS3, gradeFromS3Image, gradeFromUploadedFile } from '../services/gradingApi';
import { getStoredToken } from '../services/api';
import useImagePipeline from './useImagePipeline';
import { trackEvent as sdkTrackEvent, getDeviceContext } from '../services/telemetrySDK';

const STATES = {
  IDLE: 'idle',
  CAPTURING: 'capturing',
  PROCESSING_IMAGE: 'processing_image',
  UPLOADING: 'uploading',
  GRADING: 'grading',
  RESULTS: 'results',
  FEEDBACK: 'feedback',
  ERROR: 'error',
};

/**
 * ⛔⛔ BETA-COMMERCE-298 (P0-1) — THIS USED TO BE THE DEFAULT EXPORT, AND THAT WAS THE STALL.
 *
 * It is a plain hook over `useState`, so EVERY component that called it got its OWN instance,
 * starting at IDLE. `GradingUploadCapture` ran the grade on its instance; `GradingProcessing`
 * watched a DIFFERENT instance that never left IDLE, so its
 * `if (state === STATES.RESULTS && result) navigate(...)` never fired, and `GradeResults` — which
 * reads `location.state?.result` — got null and rendered "No grade result available".
 *
 * The grade was computed correctly and then thrown away between two React components.
 *
 * It is now the STATE FACTORY, called exactly once by `GradingFlowProvider`. The default export
 * below reads the shared instance, so neither consumer needed to change.
 */
export function useGradingFlowState() {
  const [state, setState] = useState(STATES.IDLE);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const abortRef = useRef(null);
  const flowStartRef = useRef(null);
  const { processImage } = useImagePipeline();

  const resetFlow = useCallback(() => {
    setState(STATES.IDLE);
    setProgress(0);
    setResult(null);
    setError(null);
    setProcessedImage(null);
    flowStartRef.current = null;
  }, []);

  const cancelFlow = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    resetFlow();
  }, [resetFlow]);

  const startCapture = useCallback(() => {
    setState(STATES.CAPTURING);
    flowStartRef.current = Date.now();
  }, []);

  const startUpload = useCallback(async (blob, source = 'library') => {
    if (!blob) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const deviceCtx = getDeviceContext();
    const deviceType = deviceCtx?.device_type || 'desktop';

    // Get original dimensions
    const img = await createImageBitmap(blob);
    const originalDims = { width: img.width, height: img.height };

    // Emit grade.upload.started
    sdkTrackEvent('grade.upload.started', 'grading', {
      source,
      file_size: blob.size,
      dims: originalDims,
      device: deviceType,
      mime_type: blob.type || 'image/jpeg',
    });

    try {
      // Process image through pipeline
      setState(STATES.PROCESSING_IMAGE);
      setProgress(10);
      const pipelineResult = await processImage(blob);
      setProcessedImage(pipelineResult);
      setProgress(25);

      const contentType = pipelineResult.format === 'webp' ? 'image/webp' : 'image/jpeg';

      // G3: BRANCH, DO NOT REPLACE.
      //
      // Both halves of the S3 flow are auth-gated -- getUploadUrl is
      // Depends(get_current_user) at grading.py:76 and gradeFromS3Image at :400 --
      // so an anonymous visitor cannot obtain a presigned URL at all. The direct
      // multipart endpoint /grading/image-estimate/upload is the ONLY anonymous
      // route, and it already carries fingerprint validation, per-IP rate limiting
      // and a 3-grade/30-day cap.
      //
      // A signed-in user MUST keep the S3 path: that is what persists the object so
      // the grade can join their collection. An anonymous grade is EPHEMERAL -- the
      // backend reads the bytes, calls the model, and never writes to S3 -- so there
      // is no object_key, no collection entry, no vault linkage, no history. That is
      // a real product difference and the UI says so rather than hiding it.
      if (!getStoredToken()) {
        setState(STATES.UPLOADING);
        setProgress(40);
        const anonFile = new File(
          [pipelineResult.processed],
          `grade.${pipelineResult.format === 'webp' ? 'webp' : 'jpg'}`,
          { type: contentType }
        );

        setState(STATES.GRADING);
        setProgress(70);
        const anonGrade = await gradeFromUploadedFile(anonFile);
        setProgress(100);

        sdkTrackEvent('grade.upload.completed', 'grading', {
          duration_ms: Date.now() - flowStartRef.current,
          retry_count: 0,
          final_dims: pipelineResult.finalDims,
          format: pipelineResult.format,
          anonymous: true,
        });

        setResult({
          ...anonGrade,
          // NO objectKey ON PURPOSE. Nothing was stored, so anything keyed off it --
          // createProductFromGrade, collection persistence -- must not silently
          // receive a stale or invented key.
          objectKey: null,
          anonymous: true,
          thumbnail: pipelineResult.thumbnail,
          timeToResult: Date.now() - flowStartRef.current,
        });
        setState(STATES.RESULTS);
        return;
      }

      // ---- AUTHENTICATED PATH BELOW IS UNCHANGED ----
      // Get presigned URL
      setState(STATES.UPLOADING);
      const uploadData = await getUploadUrl(contentType);
      setProgress(30);

      // Upload to S3
      const uploadStart = Date.now();
      let retryCount = 0;
      const maxRetries = 3;

      const doUpload = async () => {
        try {
          await uploadToS3(
            uploadData.upload_url,
            pipelineResult.processed,
            contentType,
            (pct) => setProgress(30 + Math.round(pct * 0.35))
          );
        } catch (uploadErr) {
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(r => setTimeout(r, Math.pow(2, retryCount) * 1000));
            return doUpload();
          }
          throw uploadErr;
        }
      };

      await doUpload();
      setProgress(65);

      // Emit grade.upload.completed
      sdkTrackEvent('grade.upload.completed', 'grading', {
        duration_ms: Date.now() - uploadStart,
        retry_count: retryCount,
        final_dims: pipelineResult.finalDims,
        compression: {
          original_bytes: blob.size,
          final_bytes: pipelineResult.processed.size,
          ratio: pipelineResult.compressionRatio,
        },
        format: pipelineResult.format,
        object_key: uploadData.object_key,
      });

      // Request grade
      setState(STATES.GRADING);
      setProgress(70);
      const gradeResult = await gradeFromS3Image(uploadData.object_key);
      setProgress(100);

      setResult({
        ...gradeResult,
        objectKey: uploadData.object_key,
        thumbnail: pipelineResult.thumbnail,
        timeToResult: Date.now() - flowStartRef.current,
      });
      setState(STATES.RESULTS);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err.message || 'Grading failed');
      setState(STATES.ERROR);
    } finally {
      abortRef.current = null;
    }
  }, [processImage]);

  return {
    state,
    progress,
    result,
    error,
    processedImage,
    startCapture,
    startUpload,
    cancelFlow,
    resetFlow,
    STATES,
  };
}

export const GradingFlowContext = createContext(null);

/**
 * The grading flow, shared across the /maestro/grade/* routes.
 *
 * ⛔ THROWS WHEN THERE IS NO PROVIDER, DELIBERATELY. The alternative — quietly falling back to a
 * fresh local instance — is exactly the bug this replaces: a component silently gets its own IDLE
 * state and the flow stalls with no error anywhere. A missing provider is a wiring mistake, and a
 * wiring mistake must be VISIBLE.
 *
 * ⚠ `/maestro/grade/*` is declared TWICE in App.jsx (the signed-out tree and the signed-in tree
 * inside <IdentityProvider>). BOTH must be wrapped; wrapping one leaves the other throwing.
 * Guarded by `src/__tests__/unit/beta_commerce/App.gradingFlowProvider.test.jsx`, which asserts
 * every /maestro/grade route in App.jsx sits inside a provider — in BOTH trees.
 */
export default function useGradingFlow() {
  const ctx = useContext(GradingFlowContext);
  if (ctx === null) {
    throw new Error(
      'useGradingFlow() called outside <GradingFlowProvider>. The /maestro/grade/* routes must be ' +
        'wrapped in BOTH route trees in App.jsx, or upload/processing/results each get their own ' +
        'state and the flow stalls (BETA-COMMERCE-298 P0-1).'
    );
  }
  return ctx;
}

export { STATES };
